/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
/* eslint-disable max-len */

import {
  DELIMITER,
  // SM_FUNCS,
  // RECENT_ORDERS_TYPENAME,
  // RECENTLY_VIEWED_SCHEMA_NAME,
  CHANNEL_OPTIONS,
  DEFAULT_OPERATIONS,
} from './Constants';

export const MAX_COLS = 12;
export const MIN_SLOT_SPACE = MAX_COLS / 3; // min needed for a component
export const MED_SLOT_SPACE = MAX_COLS / 2;

const fullSlotWillBeAvailable = (num) => {
  return num > 0 && (num % MIN_SLOT_SPACE === 0 || num % MED_SLOT_SPACE === 0);
};

const getExpandedComponentAndUpdatedSpace = (
  component,
  space,
  mediaQuery = 'minWidthLarge',
) => {
  const componentCopy = JSON.parse(JSON.stringify(component));
  const { min, max } = componentCopy.sizes[mediaQuery];
  let mediaQueryComponentWidth = min;

  if (space) {
    if (max - min === space || fullSlotWillBeAvailable(space - (max - min))) {
      mediaQueryComponentWidth = max;
    } else if (
      max - min < space
      && fullSlotWillBeAvailable(space - (max - min))
    ) {
      mediaQueryComponentWidth = space + min;
    } else if (space < max) {
      mediaQueryComponentWidth = space + min;
    }
  }

  if (!componentCopy.renderWidths) {
    componentCopy.renderWidths = {};
  }

  componentCopy.renderWidths[mediaQuery] = mediaQueryComponentWidth;

  return {
    updatedComponent: componentCopy,
    updatedSpace: space - (mediaQueryComponentWidth - min),
  };
};

const getSpaceLeftInRow = (row, mediaQuery = 'minWidthLarge') => {
  if (!row) return MAX_COLS;
  const rowCopy = JSON.parse(JSON.stringify(row));
  const spaceLeft = rowCopy.reduce((acc, curr) => {
    if (!curr) return acc;
    return acc - (curr.renderWidths[mediaQuery] || curr.sizes[mediaQuery].min);
  }, MAX_COLS);

  return spaceLeft;
};

const sortByWeight = (componentArr) => {
  return componentArr.sort((compA, compB) => {
    const { weight: aWeight = 0 } = compA.position;
    const { weight: bWeight = 0 } = compB.position;
    if (aWeight > bWeight) {
      return -1;
    }

    if (aWeight < bWeight) {
      return 1;
    }

    return 0;
  });
};

const canAllComponentsFitSize = (row, size, mediaQuery = 'minWidthLarge') => {
  const maxTotal = row.reduce((acc, comp) => {
    if (!comp) return acc;
    return acc - comp.sizes[mediaQuery][size];
  }, MAX_COLS);

  return (
    maxTotal >= 0
    && (maxTotal / MIN_SLOT_SPACE === 0 || maxTotal / MED_SLOT_SPACE === 0)
  );
};

const splitOutComponentsByLockedAndUnlocked = (components) => {
  const lockedComponents = [];
  const unlockedComponents = components
    ? components.reduce((all, curr) => {
      if (!curr.position?.locked) {
        all.push(curr);
      } else {
        lockedComponents.push(curr);
      }

      return all;
    }, [])
    : [];

  return {
    lockedComponents,
    unlockedComponents,
  };
};

const updateComponentRenderWidth = (
  allComponents,
  componentId,
  renderWidths,
) => {
  const idx = allComponents.findIndex(({ id }) => id === componentId);
  allComponents[idx].renderWidths = renderWidths;

  return allComponents;
};

const findEmptySlotInRow = (
  row,
  sizeRequired,
  mediaQuery = 'minWidthLarge',
) => {
  if (!row.length) return 0;
  const slotsUsed = [];
  let slotsNeededCount = sizeRequired;

  let idx = 0;

  while (idx < MAX_COLS && slotsNeededCount > 0) {
    if (!row[idx]) {
      slotsUsed.push(0);
      slotsNeededCount--;
      idx++;
    } else {
      let size = row[idx].sizes[mediaQuery].min;

      while (size) {
        slotsUsed.push(1);
        // eslint-disable-next-line no-plusplus
        size--;
        idx++;
      }
    }
  }

  return slotsNeededCount <= 0 && slotsUsed.findIndex((status) => status === 0);
};

const placeComponentInNextAvailableRow = ({
  graph,
  rowIdx,
  component,
  allComponents,
  mediaQuery = 'minWidthLarge',
}) => {
  const newGraph = JSON.parse(JSON.stringify(graph));
  const spaceLeftInRow = getSpaceLeftInRow(newGraph[rowIdx], mediaQuery);
  let updatedComponent = JSON.parse(JSON.stringify(component));
  const { position, sizes, renderWidths } = updatedComponent;
  const { min: myMin } = sizes[mediaQuery];
  if (!newGraph[rowIdx]) {
    newGraph[rowIdx] = new Array(MAX_COLS);
    updatedComponent = {
      ...updatedComponent,
      originalPosition: position,
      position: {
        ...position,
        yPos: rowIdx,
        xPos: position?.xPos,
      },
      renderWidths: {
        ...renderWidths,
        [mediaQuery]: myMin,
      },
    };

    newGraph[rowIdx][position?.xPos] = updatedComponent;
    updateComponentRenderWidth(
      allComponents,
      component.id,
      updatedComponent.renderWidths,
    );

    return {
      graph: newGraph,
      allComponents,
    };
  }

  const row = newGraph[rowIdx];

  if (spaceLeftInRow >= myMin) {
    const slot = findEmptySlotInRow(row, myMin);
    if (slot || slot === 0) {
      updatedComponent = {
        ...updatedComponent,
        originalPosition: position,
        position: {
          ...position,
          yPos: rowIdx,
          xPos: slot,
        },
        renderWidths: {
          ...renderWidths,
          [mediaQuery]: myMin,
        },
      };

      row[slot] = updatedComponent;
      newGraph[rowIdx] = row;
      updateComponentRenderWidth(
        allComponents,
        component.id,
        updatedComponent.renderWidths,
      );

      return {
        graph: newGraph,
        allComponents,
      };
    }

    return placeComponentInNextAvailableRow({
      graph: newGraph,
      rowIdx: rowIdx + 1,
      component,
      mediaQuery,
      allComponents,
    });
  }

  return placeComponentInNextAvailableRow({
    graph: newGraph,
    rowIdx: rowIdx + 1,
    component,
    mediaQuery,
    allComponents,
  });
};

const canComponentFit = (space, component, mediaQuery = 'minWidthLarge') => {
  const { min } = component.sizes[mediaQuery];

  return (
    min <= space && (min % MIN_SLOT_SPACE === 0 || min % MED_SLOT_SPACE === 0)
  );
};

// **********************************************
// Operations available to use to create a layout
// **********************************************

const excludeUnwantedComponents = (
  graph,
  components,
  orphanedComponents,
  _mediaQuery = 'minWidthLarge',
) => {
  const newGraph = JSON.parse(JSON.stringify(graph));
  const orphans = JSON.parse(JSON.stringify(orphanedComponents)).filter(
    (component) => component && !component.exclude,
  );
  const updatedComponents = JSON.parse(JSON.stringify(components)).filter(
    (component) => component && !component.exclude,
  );

  Object.entries(newGraph).forEach(([key, componentsArr]) => {
    // preferred name vs includedComponents, since these are actively excluded, not actively included
    const nonExcludedComponents = componentsArr.filter(
      (component) => component && !component.exclude,
    );
    newGraph[key] = nonExcludedComponents;
  });

  return {
    graph: newGraph,
    orphanedComponents: orphans,
    components: updatedComponents,
  };
};

const fillInOrphanedComponents = (
  graph,
  allComponents,
  orphanedComponents,
  mediaQuery = 'minWidthLarge',
) => {
  let filledInGraph = JSON.parse(JSON.stringify(graph));

  orphanedComponents.forEach((component) => {
    const { yPos } = component.position;

    const result = placeComponentInNextAvailableRow({
      graph: filledInGraph,
      rowIdx: yPos,
      component,
      mediaQuery,
      allComponents,
    });

    filledInGraph = result.graph;
    allComponents = result.allComponents;
  });

  return {
    graph: filledInGraph,
    orphanedComponents: [],
    components: allComponents,
  };
};

const placeLockedComponentsInGrid = (
  graph,
  allComponents,
  orphans = [],
  mediaQuery = 'minWidthLarge',
) => {
  const { lockedComponents } = splitOutComponentsByLockedAndUnlocked(allComponents);
  const newGraph = JSON.parse(JSON.stringify(graph));
  const orphanedComponents = [...orphans];
  const sortedLockedComponents = sortByWeight(lockedComponents);

  for (let idx = 0; idx < sortedLockedComponents.length; idx++) {
    const component = JSON.parse(JSON.stringify(sortedLockedComponents[idx]));
    const { xPos, yPos } = component.position || {};

    if (!component.renderWidths) {
      component.renderWidths = {};
    }
    component.renderWidths[mediaQuery] = component.sizes[mediaQuery].min;

    if (!newGraph[yPos]) {
      newGraph[yPos] = new Array(MAX_COLS);
    }

    const row = newGraph[yPos];
    const spaceForComponent = getSpaceLeftInRow(row, mediaQuery);
    const componentCanFit = canComponentFit(
      spaceForComponent,
      component,
      mediaQuery,
    );

    if (!componentCanFit || row[xPos]) {
      orphanedComponents.push(component);
    } else {
      row[xPos] = component;
    }

    updateComponentRenderWidth(
      allComponents,
      component.id,
      component.renderWidths,
    );
  }

  return {
    graph: newGraph,
    orphanedComponents,
    components: allComponents,
  };
};

const placeUnlockedComponentsInGrid = (
  graph,
  allComponents,
  orphans = [],
  mediaQuery = 'minWidthLarge',
) => {
  const { unlockedComponents } = splitOutComponentsByLockedAndUnlocked(allComponents);
  let graphCopy = JSON.parse(JSON.stringify(graph));
  const sortedComponents = sortByWeight(unlockedComponents);
  const orphanedComponents = [...orphans]; // components that don't fit where they should

  sortedComponents.forEach((component) => {
    const updatedComponent = JSON.parse(JSON.stringify(component));
    const { xPos, yPos } = component.position || {};

    if (!updatedComponent.renderWidths) {
      updatedComponent.renderWidths = {};
    }

    updatedComponent.renderWidths[mediaQuery] = updatedComponent.sizes[mediaQuery].min;

    if (!graphCopy[yPos]) {
      graphCopy[yPos] = new Array(MAX_COLS);
      graphCopy[yPos][xPos] = updatedComponent;
      return;
    }

    const row = graphCopy[yPos];
    const spaceForComponent = getSpaceLeftInRow(graphCopy[yPos], mediaQuery);
    const componentCanFit = canComponentFit(
      spaceForComponent,
      updatedComponent,
      mediaQuery,
    );

    if (!row[xPos] && componentCanFit) {
      row[xPos] = updatedComponent;
      graphCopy[yPos] = row;
    } else {
      orphanedComponents.push(updatedComponent);
      // graphCopy = placeComponentInNextAvailableRow(graphCopy, yPos, component, mediaQuery);
    }

    updateComponentRenderWidth(
      allComponents,
      component.id,
      updatedComponent.renderWidths,
    );
  });

  return {
    graph: graphCopy,
    orphanedComponents,
    components: allComponents,
  };
};
const expandRows = (
  graph,
  allComponents,
  orphans = [],
  mediaQuery = 'minWidthLarge',
) => {
  const componentGraph = JSON.parse(JSON.stringify(graph));
  Object.entries(componentGraph).forEach(([key, row]) => {
    let updatedRow = [];
    let space = getSpaceLeftInRow(componentGraph[key], mediaQuery);

    if (space > 0) {
      if (canAllComponentsFitSize(row, 'max')) {
        row.forEach((component) => {
          if (!component) return;
          const updatedComponent = {
            ...JSON.parse(JSON.stringify(component)),
            renderWidths: {
              ...component.renderWidths,
              [mediaQuery]: component?.sizes[mediaQuery]?.max,
            },
          };
          updatedRow.push(updatedComponent);
          updateComponentRenderWidth(
            allComponents,
            component.id,
            updatedComponent.renderWidths,
          );
        });
      } else {
        row.forEach((component) => {
          if (!component) return;
          const { updatedComponent, updatedSpace } = getExpandedComponentAndUpdatedSpace(component, space, mediaQuery);

          updatedRow.push(updatedComponent);
          updateComponentRenderWidth(
            allComponents,
            component.id,
            updatedComponent.renderWidths,
          );
          space = updatedSpace;
        });
      }
    } else {
      row.forEach((component) => {
        if (!component) return;
        const updatedComponent = {
          ...JSON.parse(JSON.stringify(component)),
          renderWidths: {
            ...component.renderWidths,
            [mediaQuery]: component?.sizes[mediaQuery]?.min,
          },
        };
        updatedRow.push(updatedComponent);
        updateComponentRenderWidth(
          allComponents,
          component.id,
          updatedComponent.renderWidths,
        );
      });
    }

    componentGraph[key] = updatedRow;
  });

  return {
    graph: componentGraph,
    orphanedComponents: orphans,
    components: allComponents,
  };
};

// ************************
// Default operations order
// ************************

export const LAYOUT_RULES = {
  placeLockedComponentsInGrid,
  fillInOrphanedComponents,
  placeUnlockedComponentsInGrid,
  expandRows,
  excludeUnwantedComponents,
};

// *********************************************
// Function used to ultimately create the layout
// *********************************************

export const createGraphFromLayoutOperations = ({
  allComponents,
  myComponents,
  componentOptions = {},
  layoutOperations = DEFAULT_OPERATIONS,
  mediaQuery = 'minWidthLarge',
}) => {
  if (
    !allComponents
    || !myComponents
    || !CHANNEL_OPTIONS.includes(mediaQuery)
  ) {
    return null;
  }

  let graph = {};
  let orphanedComponents = [];
  let potentialComponents = JSON.parse(JSON.stringify(allComponents));

  let components = potentialComponents.filter(
    ({ __typename, customRenderId }) => {
      // prefer customRenderId to __typename, fallback to __typename
      return !!componentOptions[customRenderId] || !!myComponents[__typename];
    },
  );

  components = components.map((component) => {
    const { customRenderId } = component;
    if (!customRenderId || !componentOptions[customRenderId]) {
      return component;
    }

    const { locked, sizes } = componentOptions[customRenderId];
    const { xPos, yPos } = locked || {};

    if (sizes) {
      component.sizes = sizes;
      if (!component.sizes.maxWidthMedium) {
        component.sizes.maxWidthMedium = { min: 12, max: 12 };
      }
    }

    if (locked) {
      component.position = {
        ...component.position,
        locked: true,
        xPos,
        yPos,
      };
    }

    return component;
  });

  layoutOperations.forEach((rule) => {
    if (LAYOUT_RULES[rule]) {
      const result = LAYOUT_RULES[rule](
        graph,
        components,
        orphanedComponents,
        mediaQuery,
      );

      graph = result.graph;
      orphanedComponents = result.orphanedComponents;
      components = result.components;
    }
  });

  return {
    components, // returned so we can build on it for other media queries
    graph,
  };
};
